import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useZoneEditor, ZONES_TAB_PATH } from './use-zone-editor';
import { ZoneEditorSkeleton } from './ZoneEditorSkeleton';
import { ZoneForm } from './ZoneForm';
import { withinSection } from '../../layouts/section-trail';
import { REFERENCE_DATA_TAB_LABELS, referenceDataTabPath } from './reference-data-tabs';

const ZONE_TRAIL = withinSection('reference-data', { label: REFERENCE_DATA_TAB_LABELS.zones, to: referenceDataTabPath('zones') });

/** `/reference-data/zones/new` and `/reference-data/zones/:id`. Keyed by id, so moving between zones starts a fresh editor. */
export function ZoneEditorPage() {
  const { id } = useParams();
  return <ZoneEditor key={id ?? 'new'} zoneId={id} />;
}

/**
 * A zone on its own page rather than in a drawer: tracing a polygon needs the room (the drawer's
 * map was 520px wide at most). Fields on the left, the map filling the rest.
 */
function ZoneEditor({ zoneId }: { zoneId: string | undefined }) {
  const { record, kindsStatus, activeKinds, mapTileKey, saveError, saving, save } = useZoneEditor(zoneId);
  const navigate = useNavigate();

  if (record.status === 'forbidden' || kindsStatus === 'forbidden') return <Navigate to="/" replace />;
  if (record.status === 'not_found') {
    return <EmptyState icon={FileX} title="Zone not found" description="This zone doesn't exist, or the link is out of date." />;
  }
  if (record.status === 'error' || kindsStatus === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this zone" description="Something went wrong loading this zone. Try reloading the page." />;
  }
  if (record.status === 'loading' || kindsStatus === 'loading' || mapTileKey === undefined) return <ZoneEditorSkeleton breadcrumbs={ZONE_TRAIL} />;

  const { zone } = record;

  return (
    <>
      <PageHeader
        breadcrumbs={ZONE_TRAIL}
        title={zone ? zone.name : 'New zone'}
        description="Draw the zone's polygon, or set a single point for a named place. Rate lines key on its code."
      />
      <ZoneForm
        zone={zone}
        zoneKinds={activeKinds}
        mapTileKey={mapTileKey}
        error={saveError}
        submitting={saving}
        onSave={save}
        onCancel={() => navigate(ZONES_TAB_PATH)}
      />
    </>
  );
}
